import { useEffect, useState } from "react";
import {
  AuthShell,
  AuthAlert,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from "@/components/auth/auth-shell";
import { LocaleLink } from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import { useLogin } from "@/hooks/use-tp-backend-auth";
import {
  clearAuthToken,
  getAuthToken,
  setAuthToken,
} from "@/common/apollo/auth-token";
import { useTranslation } from "@/common/providers/i18n-provider";
import { claimOidcAutoSubmit, submitOidcLogin } from "@/lib/oidc-handoff";

interface LoginPageProps {
  /** Where to send the user after a successful sign-in (defaults to home). */
  nextPath?: string;
  /**
   * Set when tp-backend's OIDC provider sent the user here to log in for the
   * forum: after sign-in, hand the session back to it instead of `nextPath`.
   */
  oidcUid?: string;
}

/**
 * Sign-in form, ported from tianpan-v3-web's `src/pages/auth/login.tsx`.
 * Same mechanism: `login` mutation → JWT stored in localStorage under
 * `authToken:tianpan.co` → full-page redirect to `nextPath`. The forum signs
 * in over OIDC instead of v3's DiscourseConnect `?sso=&sig=` handoff: with
 * `oidcUid`, the token is handed to tp-backend's OIDC provider rather than
 * redirecting (see `@/lib/oidc-handoff`).
 */
export function LoginPage({ nextPath = "/", oidcUid }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [handingOff, setHandingOff] = useState(false);

  const { login, loading, error } = useLogin();
  const { t } = useTranslation();

  // Already signed in on tianpan.co: continue to the forum without asking again.
  useEffect(() => {
    if (!oidcUid) return;
    const token = getAuthToken();
    if (!token) return;
    if (claimOidcAutoSubmit(oidcUid)) submitOidcLogin(oidcUid, token);
    else clearAuthToken(); // tp-backend already rejected it for this login
  }, [oidcUid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const result = await login({ variables: { email, password } });

      if (result.data?.login) {
        const { token } = result.data.login;
        if (token) setAuthToken(token);
        if (oidcUid && token) {
          setHandingOff(true);
          submitOidcLogin(oidcUid, token);
        } else {
          window.location.href = nextPath;
        }
      }
    } catch {
      setErrorMessage(t("auth.login.failed"));
    }
  };

  return (
    <AuthShell
      eyebrow={t("auth.login.eyebrow")}
      title={t("auth.login.title")}
      subtitle={
        oidcUid
          ? t("auth.login.subtitleForum")
          : t("auth.login.subtitle")
      }
      footer={
        <>
        <LocaleLink
          to={
            oidcUid
              ? `/auth/sign-up?next=${encodeURIComponent(`/auth/login?oidc=${oidcUid}`)}`
              : "/auth/sign-up"
          }
          className="text-primary hover:underline"
        >
          {t("auth.login.toSignUp")}
        </LocaleLink>
        {!oidcUid && (
          <LocaleLink
            to="/auth/forgot-password"
            className="text-muted-foreground hover:text-foreground hover:underline"
          >
            {t("auth.login.forgotPassword")}
          </LocaleLink>
        )}
        </>
      }
    >
      <AuthAlert message={errorMessage || error?.message} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className={AUTH_LABEL_CLASS}>
            {t("auth.field.email")}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={AUTH_INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="password" className={AUTH_LABEL_CLASS}>
            {t("auth.field.password")}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="••••••••"
            className={AUTH_INPUT_CLASS}
          />
        </div>

        <Button
          type="submit"
          disabled={loading || handingOff}
          className="w-full"
          size="lg"
        >
          {handingOff
            ? t("auth.login.handingOff")
            : loading
              ? t("auth.login.submitting")
              : t("auth.login.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
